import type { Locale } from './i18n';

/**
 * Waste calendars from any provider (W2): almost every German waste company offers its pick-up
 * dates as a calendar link, but each words them differently ("Restabfall 14-tägl.", "Abfuhr
 * Bioabfall", "Gelbe Säcke"). These rules turn the titles into the few bins a family knows, so
 * hints and the "… rausstellen" task read the same everywhere.
 */
const KINDS: { label: string; match: RegExp }[] = [
  { label: 'Restmüll', match: /rest(müll|abfall|-\s)|hausmüll|graue tonne|schwarze tonne/i },
  { label: 'Bio', match: /\bbio(tonne|müll|abfall|gut|abfälle)?\b|braune tonne|kompost/i },
  { label: 'Papier', match: /papier|pappe|\bppk\b|blaue tonne/i },
  { label: 'Gelber Sack', match: /gelbe[rn]? s(a|ä)ck/i },
  { label: 'Gelbe Tonne', match: /gelbe tonne|leichtverpackung|\blvp\b|verpackung/i },
  { label: 'Wertstofftonne', match: /wertstoff/i },
  { label: 'Glas', match: /\b(alt)?glas\b|glascontainer/i },
  { label: 'Sperrmüll', match: /sperr(müll|gut)/i },
  { label: 'Schadstoffmobil', match: /schadstoff|problemabfall|umweltmobil|giftmobil/i },
  {
    label: 'Grünschnitt',
    match: /grün(schnitt|abfall|gut)|garten(abfall|abfälle)|\blaub|strauchschnitt|baumschnitt/i,
  },
  { label: 'Weihnachtsbaum', match: /weihnachtsb(a|ä)um|tannenb(a|ä)um|christb(a|ä)um/i },
  { label: 'Windeltonne', match: /windel/i },
];

/** Generic words of waste calendars – a title with one of these is about collection too. */
const WASTE_WORDS = /müll|abfall|abfälle|tonne|abfuhr|entsorgung|wertstoff|recycling/i;

/** "Restabfall 14-täglich" → "Restmüll"; titles naming several bins or none stay as they are. */
export function binLabel(title: string): string {
  let kinds = KINDS.filter((k) => k.match.test(title)).map((k) => k.label);
  // "Gelber Sack (Leichtverpackungen)" is one bin, not two
  if (kinds.includes('Gelber Sack')) kinds = kinds.filter((k) => k !== 'Gelbe Tonne');
  if (kinds.length === 1) return kinds[0]!;
  return (
    title
      // container sizes ("80l-240l") and rhythm words say nothing to the family
      .replace(/\s*\d+\s*l?\s*-\s*\d+\s*l\b/gi, '')
      .replace(/\s*\(?\s*\d+[-\s]?(tägl(ich)?|wöchentl(ich)?)\.?\s*\)?/gi, '')
      .replace(/^\s*(abfuhr|leerung|abholung)\s*:?\s*/i, '')
      .trim() || title.trim()
  );
}

/**
 * A pick-up of one or more bins – not a note like "Feiertag: Abfuhr verschiebt sich", which
 * stays an ordinary date (and is then mentioned the day before like any special event).
 */
export function isBinTitle(title: string): boolean {
  return KINDS.some((k) => k.match.test(title));
}

export function isWasteTitle(title: string): boolean {
  return KINDS.some((k) => k.match.test(title)) || WASTE_WORDS.test(title);
}

/**
 * A calendar looks like waste collection when most of its dates are bins (one stray note about
 * holiday shifts is fine); a school calendar with a single "Altpapiersammlung" is not.
 */
export function looksLikeWasteCalendar(titles: string[]): boolean {
  if (titles.length < 2) return titles.length === 1 && isWasteTitle(titles[0]!);
  return titles.filter(isWasteTitle).length / titles.length >= 0.6;
}

/** Bins that go out to the street the evening before (not drop-off dates or the mobile). */
export function isCurbside(label: string): boolean {
  return !/anlieferung|mobil|sperr|glas/i.test(label);
}

/**
 * English names of the bins (1.19, British: "general waste", "bin", "nappy"). German stays the
 * stored label – the waste companies speak German; only what the family reads is translated.
 */
export const BIN_TEXT = {
  en: {
    names: {
      Restmüll: 'General waste',
      Bio: 'Food waste',
      Papier: 'Paper',
      'Gelber Sack': 'Yellow bags',
      'Gelbe Tonne': 'Yellow bin',
      Wertstofftonne: 'Recycling bin',
      Glas: 'Glass',
      Sperrmüll: 'Bulky waste',
      Schadstoffmobil: 'Hazardous waste van',
      Grünschnitt: 'Garden waste',
      Weihnachtsbaum: 'Christmas tree',
      Windeltonne: 'Nappy bin',
    } as Record<string, string>,
    /** "Put out the yellow bags tonight" reads naturally for every bin name. */
    putOut: (name: string) => `Put out: ${name}`,
  },
};

/** What the family reads for a bin label: German as stored, English translated if known. */
export function binName(label: string, locale: Locale = 'de'): string {
  return locale === 'de' ? label : (BIN_TEXT.en.names[label] ?? label);
}

/** Text of the evening-before task: "Gelbe Säcke rausstellen", "Bio-Tonne rausstellen". */
export function binReminderTitle(label: string, locale: Locale = 'de'): string {
  if (locale === 'en') return BIN_TEXT.en.putOut(binName(label, locale));
  if (label === 'Gelber Sack') return 'Gelbe Säcke rausstellen';
  if (/tonne|sack|säcke|baum|schnitt/i.test(label)) return `${label} rausstellen`;
  if (KINDS.some((k) => k.label === label)) return `${label}-Tonne rausstellen`;
  return `${label} rausstellen`;
}
